<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">科技敏感问题审查</h1>
        <p class="text-sm text-slate-500 mt-1">识别项目是否涉及国家机密、伦理争议或敏感生物技术等</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">申报初审</span>
        <span class="px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-semibold">发现问题</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Check Items -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查项配置</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要审查的敏感问题项目，系统将对申报书进行多维度敏感问题审查</p>

        <div class="ml-10 space-y-3">
          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('secrecy') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('secrecy')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('secrecy') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('secrecy') ? 'text-blue-700' : 'text-slate-700'">国家机密审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('secrecy') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查申报书内容是否涉及国家机密、技术秘密等敏感信息</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('ethics') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('ethics')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('ethics') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('ethics') ? 'text-blue-700' : 'text-slate-700'">伦理争议审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('ethics') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查项目是否涉及伦理争议、人体实验、动物实验等敏感领域</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('biotech') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('biotech')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('biotech') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('biotech') ? 'text-blue-700' : 'text-slate-700'">敏感生物技术审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('biotech') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查项目是否涉及基因编辑、生物安全等敏感生物技术</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('cooperation') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('cooperation')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('cooperation') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('cooperation') ? 'text-blue-700' : 'text-slate-700'">国际合作审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('cooperation') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查国际合作单位背景、合作内容是否存在安全风险</p>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">申报书信息输入</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入申报书内容或上传文件，进行科技敏感问题审查分析</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="declarationContent"
              class="premium-input w-full h-40 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请粘贴申报书内容..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ declarationContent.length }} 字符
            </div>
          </div>

          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              上传文件
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX 格式</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          启动敏感问题审查
        </button>
      </div>
    </div>

    <!-- Check Results -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查结果</h2>
        </div>
        <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          导出报告
        </button>
      </div>

      <!-- Sensitive Check Title -->
      <h3 class="text-xl font-bold text-slate-800 mb-4">科技敏感问题审查</h3>

      <div class="space-y-4">
        <!-- Sensitive Check Result - Found Issues -->
        <div class="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-semibold text-red-800 text-sm">检查结果：发现问题</h4>
              <p class="text-xs text-red-600 mt-1">申报书中涉及部分可能敏感的技术描述，需要进一步核实和说明。</p>
            </div>
          </div>
        </div>

        <!-- Sensitive Points Identification -->
        <div class="ml-4">
          <h5 class="font-semibold text-slate-700 text-sm mb-3">敏感点识别：</h5>
          <ul class="space-y-2 text-sm text-slate-600">
            <li class="flex items-start gap-2">
              <span class="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0"></span>
              <span>第12页提到的"特殊制备工艺"可能涉及技术秘密</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0"></span>
              <span>国际合作单位背景需要进一步说明</span>
            </li>
          </ul>
        </div>

        <!-- Processing Suggestions -->
        <div class="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
            <h4 class="font-semibold text-blue-800 text-sm">处理建议</h4>
          </div>
          <ol class="space-y-2 text-sm text-blue-700 ml-6 list-decimal">
            <li>对敏感技术描述进行脱敏处理或提供详细说明</li>
            <li>补充国际合作单位的资质证明和合作协议</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeclareSensitiveCheck',
  data() {
    return {
      selectedChecks: ['secrecy', 'ethics', 'biotech', 'cooperation'],
      declarationContent: ''
    }
  },
  methods: {
    toggleCheck(check) {
      const index = this.selectedChecks.indexOf(check)
      if (index > -1) {
        this.selectedChecks.splice(index, 1)
      } else {
        this.selectedChecks.push(check)
      }
    }
  }
}
</script>